import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, Zap, Clock, TrendingUp } from "lucide-react";
import type { DamageCalculationResult } from "@/lib/damage-calculator/damage-calculator-utils";
import type { WeaponData } from "@/lib/types/weapon";

interface DamageCalculatorResultsProps {
  results: DamageCalculationResult[];
  weapons: WeaponData[];
  individualWeaponResults?: DamageCalculationResult[];
}

export function DamageCalculatorResults({
  results,
  weapons,
  individualWeaponResults,
}: DamageCalculatorResultsProps) {
  // Filter out results that can't target
  const validResults = results.filter((r) => r && r.canTarget);
  
  if (validResults.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">Can't target</p>
            <p className="text-xs mt-1">Selected weapons cannot engage this target</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate combined totals
  const totalDps = validResults.reduce((sum, r) => sum + r.damagePerSecond, 0);
  const totalAverageDps = validResults.reduce((sum, r) => sum + r.averageDamagePerSecond, 0);
  const totalSuppressDps = validResults.reduce(
    (sum, r) => sum + (r.suppressionDamage / Math.max(r.suppressionTimeToKill, 0.001)),
    0
  );

  // Use the combined time to kill from merged events (all weapons fire simultaneously)
  const optimalTimeToKill = validResults[0]?.timeToKill || 0;
  const averageTimeToKill = validResults[0]?.averageTimeToKill || 0;
  const optimalTimeToSuppress = Math.max(validResults[0]?.suppressionTimeToKill || 0, 0);
  const averageTimeToSuppress = Math.max(validResults[0]?.suppressionTimeToKillWithAccuracy || 0, 0);

  // Build weapon comparison table
  // Use individual weapon results for each weapon's stats
  const weaponComparisonRows = [
    { label: "Weapon", values: [] as string[] },
    { label: "Damage per shot", values: [] as string[] },
    { label: "Suppression per shot", values: [] as string[] },
    { label: "Accuracy", values: [] as string[] },
    { label: "Optimal DPS", values: [] as string[] },
    { label: "Average DPS", values: [] as string[] },
    { label: "Optimal Suppress DPS", values: [] as string[] },
    { label: "Average Suppress DPS", values: [] as string[] },
    { label: "Missile Speed", values: [] as string[] },
    { label: "Missile Acceleration", values: [] as string[] },
    { label: "Missile Travel Time", values: [] as string[] },
  ];

  const combinedResult = validResults[0];
  const individualResults = individualWeaponResults || [];

  for (let i = 0; i < weapons.length; i++) {
    const weapon = weapons[i];
    const individualResult = individualResults[i];

    if (!weapon) {
      continue;
    }

    weaponComparisonRows[0]!.values.push(weapon.ammo.name);

    if (!individualResult || !individualResult.canTarget) {
      for (let j = 1; j < weaponComparisonRows.length; j++) {
        weaponComparisonRows[j]!.values.push("Cannot target unit");
      }
      continue;
    }

    // Use individual weapon result for this weapon's stats
    weaponComparisonRows[1]!.values.push(individualResult.damage.toFixed(3));
    weaponComparisonRows[2]!.values.push(individualResult.suppressionDamage.toFixed(3));
    weaponComparisonRows[3]!.values.push(`${individualResult.accuracy.toFixed(3)} %`);
    weaponComparisonRows[4]!.values.push(individualResult.damagePerSecond.toFixed(3));
    
    const avgDps = (individualResult.shotsToKillWithAccuracy * individualResult.damage) / Math.max(individualResult.averageTimeToKill, 0.001);
    weaponComparisonRows[5]!.values.push(avgDps.toFixed(3));
    
    const optimalSuppressDps = individualResult.suppressionDamage / Math.max(individualResult.suppressionTimeToKill, 0.001);
    weaponComparisonRows[6]!.values.push(optimalSuppressDps.toFixed(3));
    
    const avgSuppressDps = individualResult.suppressionDamage / Math.max(individualResult.suppressionTimeToKillWithAccuracy, 0.001);
    weaponComparisonRows[7]!.values.push(avgSuppressDps.toFixed(3));

    if (weapon.missile) {
      weaponComparisonRows[8]!.values.push(`${weapon.missile.maxMissileSpeed?.toFixed(3) || 0} m/s`);
      weaponComparisonRows[9]!.values.push(`${weapon.missile.maxMissileAcceleration?.toFixed(3) || 0} m/s²`);
      weaponComparisonRows[10]!.values.push(`${individualResult.flightTimeOfOneMissile?.toFixed(3) || 0} s`);
    } else {
      weaponComparisonRows[8]!.values.push("");
      weaponComparisonRows[9]!.values.push("");
      weaponComparisonRows[10]!.values.push("");
    }
  }

  // Build total comparison table
  const totalComparisonRows = [
    { label: "", values: ["Optimal", "Average"] },
    {
      label: "Number of Shots to Kill",
      values: validResults[0]
        ? [validResults[0].shotsToKill.toFixed(3), validResults[0].shotsToKillWithAccuracy.toFixed(3)]
        : ["N/A", "N/A"],
    },
    {
      label: "Number of Shots to Suppress",
      values: validResults[0]
        ? [validResults[0].shotsToMaxSuppression.toFixed(3), validResults[0].shotsToMaxSuppressionWithAccuracy.toFixed(3)]
        : ["N/A", "N/A"],
    },
    {
      label: "Time to Kill",
      values: [`${optimalTimeToKill.toFixed(3)} s`, `${averageTimeToKill.toFixed(3)} s`],
    },
    {
      label: "DPS",
      values: [totalDps.toFixed(3), totalAverageDps.toFixed(3)],
    },
    {
      label: "Time to Suppress",
      values: [`${optimalTimeToSuppress.toFixed(3)} s`, `${averageTimeToSuppress.toFixed(3)} s`],
    },
    {
      label: "Suppression DPS",
      values: [totalSuppressDps.toFixed(3), totalSuppressDps.toFixed(3)],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics - Highlighted */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-primary" />
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Optimal Total DPS
              </div>
            </div>
            <div className="text-2xl font-bold text-primary">
              {totalDps.toFixed(3)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-primary" />
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Average Total DPS
              </div>
            </div>
            <div className="text-2xl font-bold text-primary">
              {totalAverageDps.toFixed(3)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Accounts for accuracy
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-primary" />
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Optimal Time to Kill
              </div>
            </div>
            <div className="text-2xl font-bold text-primary">
              {optimalTimeToKill.toFixed(3)} s
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-primary" />
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Average Time to Kill
              </div>
            </div>
            <div className="text-2xl font-bold text-primary">
              {averageTimeToKill.toFixed(3)} s
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Accounts for accuracy
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weapon Comparison Table */}
      {weapons.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Weapon Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">Stat</TableHead>
                    {weapons.map((weapon, index) => (
                      <TableHead key={index} className="text-center min-w-[100px]">
                        {weapon.ammo.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {weaponComparisonRows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {row.label}
                      </TableCell>
                      {row.values.map((value, colIndex) => (
                        <TableCell key={colIndex} className="text-center text-xs">
                          {value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Total Comparison Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Total Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Stat</TableHead>
                <TableHead className="text-center">Optimal</TableHead>
                <TableHead className="text-center">Average</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {totalComparisonRows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell className="text-xs text-muted-foreground font-medium">
                    {row.label}
                  </TableCell>
                  {row.values.map((value, colIndex) => (
                    <TableCell key={colIndex} className="text-center text-xs font-medium">
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detailed View */}
      {validResults[0] && weapons.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Target className="h-4 w-4" />
                Weapon Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Damage Multiplier
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].damageMultiplier.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Damage per shot
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].damage.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Suppression Multiplier
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].suppressionMultiplier.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Suppression per shot
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].suppressionDamage.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Accuracy
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].accuracy.toFixed(3)} %
                    </TableCell>
                  </TableRow>
                  {weapons[0]?.missile && (
                    <>
                      <TableRow>
                        <TableCell className="text-xs text-muted-foreground py-2">
                          Missile Speed
                        </TableCell>
                        <TableCell className="text-right font-medium py-2">
                          {weapons[0]!.missile.maxMissileSpeed?.toFixed(3) || 0} m/s
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs text-muted-foreground py-2">
                          Missile Acceleration
                        </TableCell>
                        <TableCell className="text-right font-medium py-2">
                          {weapons[0]!.missile.maxMissileAcceleration?.toFixed(3) || 0} m/s²
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs text-muted-foreground py-2">
                          Missile Travel Time
                        </TableCell>
                        <TableCell className="text-right font-medium py-2">
                          {validResults[0].flightTimeOfOneMissile?.toFixed(3) || 0} s
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Detailed Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Shots to Kill (Optimal)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].shotsToKill.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Shots to Kill (Average)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].shotsToKillWithAccuracy.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Time to Kill (Optimal)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].timeToKill.toFixed(3)} s
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Time to Kill (Average)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].averageTimeToKill.toFixed(3)} s
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Shots to Suppress (Optimal)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].shotsToMaxSuppression.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Shots to Suppress (Average)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {validResults[0].shotsToMaxSuppressionWithAccuracy.toFixed(3)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Time to Suppress (Optimal)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {Math.max(validResults[0].suppressionTimeToKill, 0).toFixed(3)} s
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      Time to Suppress (Average)
                    </TableCell>
                    <TableCell className="text-right font-medium py-2">
                      {Math.max(validResults[0].suppressionTimeToKillWithAccuracy, 0).toFixed(3)} s
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-muted/50 border-dashed">
        <CardContent className="pt-6">
          <div className="text-xs text-muted-foreground space-y-2">
            <p className="font-medium text-foreground/80">
              This damage calculator calculates damage from direct hits. It does not
              take into account splash damage or precision bonus for consecutive
              shots (~3% accuracy). It also does not calculate using sorting shots
              (left to right strafing). The average time to kill is likely to be
              slightly longer than you will find in game.
            </p>
            <div>
              <span className="font-medium text-foreground/80">It DOES include:</span>
              <ul className="list-disc list-inside mt-1 ml-2">
                <li>Missile travel time (only for missiles)</li>
              </ul>
            </div>
            {weapons.length > 1 && (
              <p className="text-muted-foreground/80 mt-2">
                <span className="font-medium">Note:</span> When multiple weapons are selected,
                events from all weapons are merged chronologically. Time to kill represents when
                the combined damage from all weapons kills the target.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
